import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVisitPageParams, getVisitRoutePath } from '../../pages/visits/utils';
import { VisitBucketStatusEnum, VisitBucketTypeEnum } from '@generated';
import { CheckCircle, Circle, MapPin, User } from 'lucide-react';
import { useGetFullVisitOptions } from './queries';
import { VisitProvider } from './VisitContext';
import { VisitSummaryBucket } from './buckets/summary/VisitSummaryBucket';
import { VisitNavigation } from './VisitNavigation';
import { InsetTabs } from '~/shared/components/tabs/InsetTabs';
import { VisitInterventionTasks } from './interventions/VisitInterventionTasks';
import { VisitWorkflowStageTask } from './workflow-tasks/VisitWorkflowStageTask';
import { SuspenseWrapper } from '~/shared/SuspenseWrapper';
import { PageHeader } from '~/shared/PageHeader';
import { PatientHeader } from '../admissions/PatientHeader';
import { StatusPill } from '~/shared/StatusPill';
import { CompleteOrderVisitBucket } from './orders/CompleteOrderVisitBucket';
import { VisitOasisTasks } from './oasis/VisitOasisTasks';
import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';

export function Visit() {
	const { stayId, visitId, selectedBucketId, selectedBucketItemId } = useVisitPageParams();
	const [footerEl, setFooterEl] = useState<HTMLDivElement | null>(null);
	const { data: visit } = useSuspenseQuery(useGetFullVisitOptions(visitId));
	const navigate = useNavigate();

	//if there is no selected bucket item id, go to the first bucket, first task
	const options = visit.visitBuckets;
	const firstBucket = options.toSorted((a, b) => a.bucketOrder - b.bucketOrder)[0];
	const selectedVisitBucket = selectedBucketId ? options.find(x => x.id === selectedBucketId)! : firstBucket;

	const formattedOptions = useMemo(
		() =>
			options
				.filter(x => x.visitBucketStatusId !== VisitBucketStatusEnum.OnHold)
				.map(bucket => (
					<InsetTabs.Tab key={bucket.id} id={bucket.id} isDisabled={bucket.visitBucketStatusId === VisitBucketStatusEnum.NotStarted}>
						{bucket.visitBucketStatusId === VisitBucketStatusEnum.Completed ? (
							<CheckCircle className={clsx('size-4', bucket.id === selectedVisitBucket.id ? 'text-primary-500' : 'text-[#296747]')} />
						) : (
							<Circle className={clsx('size-4', bucket.id === selectedVisitBucket.id ? 'text-primary-500' : 'text-gray-300')} />
						)}
						{bucket.bucketLabel}
					</InsetTabs.Tab>
				)),
		[options, selectedVisitBucket.id]
	);

	const onBucketChange = (newSelectedBucketId: string) => {
		const newSelectedOption = options.find(x => x.id === newSelectedBucketId);
		if (!newSelectedOption) {
			throw new Error('Selected visit bucket not found on bucket change');
		} else if (newSelectedOption.visitBucketTypeId === VisitBucketTypeEnum.Summary) {
			navigate(getVisitRoutePath(stayId, visitId, newSelectedOption.id));
		} else {
			navigate(getVisitRoutePath(stayId, visitId, newSelectedOption.id, newSelectedOption.firstBucketItemId!));
		}
	};

	return (
		<>
			<PageHeader
				title="Visit"
				statusSlot={<StatusPill intent="info">{visit.visitStatusLabel}</StatusPill>}
				description={
					<>
						<div className="hidden items-center gap-1.5 text-xs text-gray-500 sm:flex">
							<MapPin size={11} className="text-gray-400" />
							{visit.visitAddress ? visit.visitAddress.formattedAddress : 'No address associated with this visit'}
						</div>
						<div className="hidden items-center gap-1.5 text-xs text-gray-500 md:flex">
							<User size={11} className="text-gray-400" />
							{visit.performingUser.fullName}
						</div>
					</>
				}
				titleRightSlot={<PatientHeader stayId={stayId} />}
			/>
			<VisitProvider
				stayId={stayId}
				visitId={visitId}
				selectedVisitBucket={selectedVisitBucket}
				selectedBucketItemId={selectedBucketItemId ? selectedBucketItemId : selectedVisitBucket.firstBucketItemId!}
			>
				{selectedVisitBucketItem => {
					return (
						<main className="flex min-h-0 flex-1 flex-col" key={selectedVisitBucket.id}>
							<InsetTabs
								selectedKey={selectedVisitBucket.id}
								onSelectionChange={onBucketChange}
								tabs={formattedOptions}
								className="bg-white"
							>
								{selectedVisitBucket.visitBucketTypeId === VisitBucketTypeEnum.Summary ? (
									<InsetTabs.TabPanel id={selectedVisitBucket.id}>
										<VisitSummaryBucket />
									</InsetTabs.TabPanel>
								) : selectedVisitBucket.visitBucketTypeId === VisitBucketTypeEnum.CompleteOrder ? (
									<InsetTabs.TabPanel id={selectedVisitBucket.id}>
										<SuspenseWrapper
											name="VisitBucketItemWrapper"
											key={selectedBucketItemId}
											fullScreen={false}
											className="h-full"
										>
											<CompleteOrderVisitBucket />
										</SuspenseWrapper>
									</InsetTabs.TabPanel>
								) : !selectedVisitBucketItem ? (
									<div>Selected visit bucket item not found in visit</div>
								) : (
									<InsetTabs.TabPanelWithNav
										id={selectedVisitBucket.id}
										navigation={<VisitNavigation stayId={stayId} visitId={visitId} selectedBucketId={selectedVisitBucket.id} />}
									>
										<SuspenseWrapper
											name="VisitBucketItemWrapper"
											key={selectedBucketItemId}
											fullScreen={false}
											className="h-full"
										>
											{selectedVisitBucketItem.workflowStageTaskBucket ? (
												<VisitWorkflowStageTask workflowStageTask={selectedVisitBucketItem.workflowStageTask!} />
											) : selectedVisitBucketItem.interventionTaskBucket ? (
												<VisitInterventionTasks />
											) : selectedVisitBucketItem.oasisTaskBucket ? (
												<VisitOasisTasks />
											) : (
												<div>Unknown bucket item type</div>
											)}
										</SuspenseWrapper>
									</InsetTabs.TabPanelWithNav>
								)}
							</InsetTabs>
						</main>
					);
				}}
			</VisitProvider>
		</>
	);
}
